import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { ArrowLeft, Edit } from 'lucide-react';
import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

const categorySchema = z.object({
  title: z.string().min(1, 'Category Title is required'),
  route: z.string().min(1, 'Category Route is required'),
  vertical: z.string().optional(),
  categoryName: z.string().optional(),
  subCategory: z.string().optional(),
  subSubCategory: z.string().optional(),
  exampleEntities: z.string().optional(),
  combineRecommendation: z.string().optional(),
  onboardingQuestion: z.string().optional(),
});

function EditCategory() {
  const navigate = useNavigate();
  const server_url = process.env.REACT_APP_API_URI;
  const location = useLocation();
  const { catId } = location.state || {};
  const [category, setCategoryData] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(categorySchema),
  });

  useEffect(() => {
    if (!catId) {
      navigate('/admin/category');
      return;
    }
    const getCategoryById = async () => {
      try {
        const response = await axios.post(`${server_url}/category/get-by-id`, {
          catId,
        });
        const data = response.data;
        setCategoryData(data);

        // Populate form
        setValue('title', data.title || '');
        setValue('route', data.route || '');
        setValue('vertical', data.vertical || '');
        setValue('categoryName', data.categoryName || '');
        setValue('subCategory', data.subCategory || '');
        setValue('subSubCategory', data.subSubCategory || '');
        setValue('exampleEntities', data.exampleEntities || '');
        setValue('combineRecommendation', data.combineRecommendation || '');
        setValue('onboardingQuestion', data.onboardingQuestion || '');
      } catch (err) {
        console.error(err);
      }
    };
    getCategoryById();
  }, [catId, server_url, setValue, navigate]);

  const onSubmit = async (data) => {
    try {
      setErrorMessage('');
      const response = await axios.put(
        `${server_url}/category/update-category`,
        { ...data, catId },
      );

      if (response.data.status) {
        setSuccessMessage('✅ Category updated successfully!');
        setTimeout(() => {
          setSuccessMessage('');
          navigate('/admin/category');
        }, 2000);
      } else {
        setErrorMessage('⚠️ Failed to update category. Please try again.');
        setTimeout(() => setErrorMessage(''), 3000);
      }
    } catch (err) {
      console.error(err);
      setErrorMessage('⚠️ An error occurred. Please try again.');
      setTimeout(() => setErrorMessage(''), 3000);
    }
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-3xl mx-auto bg-white shadow-xl rounded-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 p-6 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Edit className="w-6 h-6" />
            Edit Category
          </h2>
          <button
            onClick={() => navigate('/admin/category')}
            className="flex items-center text-white/80 hover:text-white transition gap-1 text-sm font-medium"
          >
            <ArrowLeft className="w-4 h-4" /> Go Back
          </button>
        </div>

        <div className="p-8">
          {category && (
            <div className="bg-gray-50 p-4 rounded-lg mb-6 shadow-sm border border-gray-100 flex gap-4 text-sm">
              <span className="text-gray-500 font-medium">
                System ID:{' '}
                <span className="text-gray-900 font-mono">{category._id}</span>
              </span>
            </div>
          )}

          {successMessage && (
            <div className="mb-6 p-4 bg-green-50 text-green-700 border border-green-200 rounded-lg text-sm font-medium flex items-center">
              {successMessage}
            </div>
          )}
          {errorMessage && (
            <div className="mb-6 p-4 bg-red-50 text-red-700 border border-red-200 rounded-lg text-sm font-medium flex items-center">
              {errorMessage}
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="col-span-1 md:col-span-2 space-y-2">
                <label className="text-sm font-semibold text-gray-700">
                  Category Title *
                </label>
                <input
                  {...register('title')}
                  placeholder="e.g. Restaurants"
                  className={cn(
                    'w-full px-4 py-2 bg-gray-50 border rounded-lg focus:ring-2 focus:ring-blue-500 transition-all outline-none',
                    errors.title
                      ? 'border-red-500 ring-red-200'
                      : 'border-gray-200',
                  )}
                />
                {errors.title && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.title.message}
                  </p>
                )}
              </div>

              <div className="col-span-1 md:col-span-2 space-y-2">
                <label className="text-sm font-semibold text-gray-700">
                  Route URL *
                </label>
                <input
                  {...register('route')}
                  placeholder="e.g. /restaurants"
                  className={cn(
                    'w-full px-4 py-2 bg-gray-50 border rounded-lg focus:ring-2 focus:ring-blue-500 transition-all outline-none',
                    errors.route
                      ? 'border-red-500 ring-red-200'
                      : 'border-gray-200',
                  )}
                />
                {errors.route && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.route.message}
                  </p>
                )}
              </div>

              {/* Taxonomy Information Section */}
              <div className="col-span-1 md:col-span-2 mt-4 pt-6 border-t border-gray-100">
                <h3 className="text-lg font-bold text-gray-800 mb-4">
                  Taxonomy Hierarchy (Optional)
                </h3>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700">
                  Vertical
                </label>
                <input
                  {...register('vertical')}
                  placeholder="e.g. Eat, Life"
                  className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 transition-all outline-none"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700">
                  Parent Category
                </label>
                <input
                  {...register('categoryName')}
                  placeholder="e.g. Insurance"
                  className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 transition-all outline-none"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700">
                  Subcategory
                </label>
                <input
                  {...register('subCategory')}
                  placeholder="e.g. Auto insurance"
                  className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 transition-all outline-none"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700">
                  Sub-subcategory
                </label>
                <input
                  {...register('subSubCategory')}
                  placeholder="e.g. Personal auto / fleet"
                  className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 transition-all outline-none"
                />
              </div>

              <div className="col-span-1 md:col-span-2 space-y-2">
                <label className="text-sm font-semibold text-gray-700">
                  Example Entities
                </label>
                <input
                  {...register('exampleEntities')}
                  placeholder="e.g. Personal auto insurers; brokerages"
                  className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 transition-all outline-none"
                />
              </div>

              <div className="col-span-1 md:col-span-2 space-y-2">
                <label className="text-sm font-semibold text-gray-700">
                  Combine / Tagging Recommendation
                </label>
                <input
                  {...register('combineRecommendation')}
                  placeholder="e.g. Keep as category; tag by policy type"
                  className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 transition-all outline-none"
                />
              </div>

              <div className="col-span-1 md:col-span-2 space-y-2">
                <label className="text-sm font-semibold text-gray-700">
                  Onboarding Question
                </label>
                <input
                  {...register('onboardingQuestion')}
                  placeholder="e.g. What insurance line is this feedback about?"
                  className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 transition-all outline-none"
                />
              </div>
            </div>

            <div className="pt-6">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-blue-600 text-white font-bold py-3 px-4 rounded-xl hover:bg-blue-700 focus:ring-4 focus:ring-blue-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
              >
                {isSubmitting ? 'Updating Category...' : 'Confirm Edits'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default EditCategory;
